import React, { useState } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import axios from 'axios';

axios.defaults.withCredentials = true;

const API_URL = process.env.REACT_APP_API_URL || 'https://brown-pelican-168360.hostingersite.com/api';

export default function Register({ onSwitch }) {
    const [form, setForm] = useState({
        studio_name: '',
        studio_email: '',
        studio_phone: '',
        owner_name: '',
        password: '',
        confirmPassword: ''
    });
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!form.studio_name || !form.studio_email || !form.password) {
            return alert('Please fill in all required fields');
        }

        if (form.password !== form.confirmPassword) {
            return alert('Passwords do not match');
        }

        if (form.password.length < 8) {
            return alert('Password must be at least 8 characters');
        }

        try {
            const { data } = await axios.post(`${API_URL}/studios/register`, {
                studio_name: form.studio_name,
                studio_email: form.studio_email,
                studio_phone: form.studio_phone,
                owner_name: form.owner_name,
                password: form.password
            });

            if (data.success) {
                alert(data.message || 'Studio registered successfully! Awaiting superadmin approval.');
                onSwitch();
            } else {
                alert(data.message || 'Registration failed');
            }
        } catch (error) {
            alert('Registration error: ' + (error.response?.data?.message || error.message));
        }
    };

    return (
        <div className="flex min-h-screen bg-white font-sans animate-fade-in">
            {/* LEFT SIDE - ILLUSTRATION */}
            <div className="hidden lg:flex w-1/2 bg-[#f8f9fa] relative items-center justify-center p-12">
                <div className="absolute top-8 left-8">
                    <img
                        src="/logo-marketing.jpg"
                        alt="BookInk Logo"
                        className="h-12 w-auto object-contain"
                    />
                </div>

                <div className="relative w-full max-w-lg">
                    <img
                        src="/auth-login-illustration-light.png"
                        alt="Register Illustration"
                        className="w-full object-contain relative z-0 drop-shadow-2xl"
                    />
                </div>
            </div>

            {/* RIGHT SIDE - FORM */}
            <div className="w-full lg:w-1/2 flex items-center justify-center p-8">
                <div className="w-full max-w-md space-y-8">
                    <div className="space-y-4">
                        <h1 className="text-3xl font-bold text-slate-800">Register Your Tattoo Studio 🎨</h1>
                        <p className="text-slate-500">Start managing your studio with BOOKINK!</p>
                    </div>

                    <form onSubmit={handleSubmit} className="space-y-6">
                        <div className="space-y-2">
                            <label className="text-sm font-medium text-slate-700">Studio Name *</label>
                            <input
                                type="text"
                                className="w-full border border-slate-300 rounded-lg px-4 py-3 focus:outline-none focus:border-[#5C5CFF] focus:ring-1 focus:ring-[#5C5CFF] transition"
                                placeholder="Ink Masters Studio"
                                value={form.studio_name}
                                onChange={e => setForm({ ...form, studio_name: e.target.value })}
                                required
                            />
                        </div>

                        <div className="space-y-2">
                            <label className="text-sm font-medium text-slate-700">Studio Email *</label>
                            <input
                                type="email"
                                name="email"
                                autoComplete="email"
                                className="w-full border border-slate-300 rounded-lg px-4 py-3 focus:outline-none focus:border-[#5C5CFF] focus:ring-1 focus:ring-[#5C5CFF] transition"
                                placeholder="studio@example.com"
                                value={form.studio_email}
                                onChange={e => setForm({ ...form, studio_email: e.target.value })}
                                required
                            />
                        </div>

                        <div className="space-y-2">
                            <label className="text-sm font-medium text-slate-700">Studio Phone</label>
                            <input
                                type="tel"
                                className="w-full border border-slate-300 rounded-lg px-4 py-3 focus:outline-none focus:border-[#5C5CFF] focus:ring-1 focus:ring-[#5C5CFF] transition"
                                placeholder="+44 123 456 7890"
                                value={form.studio_phone}
                                onChange={e => setForm({ ...form, studio_phone: e.target.value })}
                            />
                        </div>

                        <div className="space-y-2">
                            <label className="text-sm font-medium text-slate-700">Owner Name</label>
                            <input
                                type="text"
                                className="w-full border border-slate-300 rounded-lg px-4 py-3 focus:outline-none focus:border-[#5C5CFF] focus:ring-1 focus:ring-[#5C5CFF] transition"
                                placeholder="John Doe"
                                value={form.owner_name}
                                onChange={e => setForm({ ...form, owner_name: e.target.value })}
                            />
                        </div>

                        <div className="space-y-2">
                            <label className="text-sm font-medium text-slate-700">Password *</label>
                            <div className="relative">
                                <input
                                    type={showPassword ? "text" : "password"}
                                    name="password"
                                    autoComplete="new-password"
                                    className="w-full border border-slate-300 rounded-lg px-4 py-3 focus:outline-none focus:border-[#5C5CFF] focus:ring-1 focus:ring-[#5C5CFF] transition pr-10"
                                    placeholder="Min. 8 characters"
                                    value={form.password}
                                    onChange={e => setForm({ ...form, password: e.target.value })}
                                    required
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPassword(!showPassword)}
                                    className="absolute right-3 top-3.5 text-slate-400 hover:text-slate-600"
                                >
                                    {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                                </button>
                            </div>
                        </div>

                        <div className="space-y-2">
                            <label className="text-sm font-medium text-slate-700">Confirm Password *</label>
                            <div className="relative">
                                <input
                                    type={showConfirmPassword ? "text" : "password"}
                                    className="w-full border border-slate-300 rounded-lg px-4 py-3 focus:outline-none focus:border-[#5C5CFF] focus:ring-1 focus:ring-[#5C5CFF] transition pr-10"
                                    placeholder="Confirm password"
                                    value={form.confirmPassword}
                                    onChange={e => setForm({ ...form, confirmPassword: e.target.value })}
                                    required
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                    className="absolute right-3 top-3.5 text-slate-400 hover:text-slate-600"
                                >
                                    {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                                </button>
                            </div>
                        </div>

                        <div className="flex items-center gap-2">
                            <input type="checkbox" className="w-4 h-4 rounded border-slate-300 text-[#5C5CFF] focus:ring-[#5C5CFF]" required />
                            <span className="text-sm text-slate-600">I agree to privacy policy & terms</span>
                        </div>

                        <button
                            type="submit"
                            className="w-full bg-[#5C5CFF] hover:bg-[#4b4be6] text-white font-bold py-3 rounded-lg shadow-lg shadow-indigo-200 transition transform active:scale-95"
                        >
                            REGISTER STUDIO
                        </button>
                    </form>

                    <div className="text-center text-sm text-slate-500">
                        Already have an account? <button onClick={onSwitch} className="text-[#5C5CFF] font-bold hover:underline">Sign in instead</button>
                    </div>

                </div>
            </div>
        </div>
    );
}
